import { defineStore } from "pinia";
import { reactive, computed } from "vue";

import OrganizationInvitationsApi from "@Modules/teams/Api/OrganizationInvitationsApi";
import ShowModal from "@Helpers/functions/showModal.js";
import InvitationPopup from "@Modules/teams/components/InvitationPopup.vue";
import { OrganizationInvitationWithSource } from "@Modules/teams/Data/Models/OrganizationInvitationModel";

export const XOrganizationInvitationsStore = defineStore("x-organization-invitations", () => {
    const api = OrganizationInvitationsApi();

    //
    // State
    const state = reactive({
        isPopupVisible: false,
    });

    //
    // Getters
    const invitations = computed<OrganizationInvitationWithSource[]>(() =>
        api.invitations.value.map((invitation) => ({ ...invitation, source: "organization" }))
    );
    const loading = computed(() => api.loading.value);
    const hasInvitations = computed(() => invitations.value.length > 0);

    const currentInvitation = computed(() => invitations.value[0] || null);

    //
    // Actions
    async function loadInvitations(): Promise<void> {
        await api.fetchInvitations();
    }

    function showNextInvitationPopup(): void {
        const invitation = currentInvitation.value;
        if (!invitation || state.isPopupVisible) {
            return;
        }

        state.isPopupVisible = true;
        ShowModal(InvitationPopup, {
            title: "Organization Invitation",
            entityName: invitation.organization.name,
            inviterName: invitation.inviter.name,
            roleName: invitation.role?.name || null,
            acceptInvitation: () => api.acceptInvitation(invitation.id),
            rejectInvitation: () => api.rejectInvitation(invitation.id),
            notifyClosed: (succeeded: boolean) => {
                state.isPopupVisible = false;
                if (succeeded) {
                    showNextInvitationPopup();
                }
            },
        });
    }

    async function initializeAndShowPopup(): Promise<void> {
        await loadInvitations();
        showNextInvitationPopup();
    }

    function reset(): void {
        state.isPopupVisible = false;
        api.reset();
    }

    return {
        invitations,
        loading,
        hasInvitations,
        currentInvitation,
        loadInvitations,
        showNextInvitationPopup,
        initializeAndShowPopup,
        reset,
        acceptInvitation: api.acceptInvitation,
        rejectInvitation: api.rejectInvitation,
        setRouter: api.setRouter,
    };
});
